import { previousCaption, type CaptionLength, type CountDisplay } from '../lib/engagementCount';
import { formatIntegerIN } from '../lib/format';
import { cn } from '../lib/utils';
import { HintTrigger } from '../components/common/HintTrigger';

const EMPTY_TEXT = '—';

/** Typography for the Creator Content count columns. */
export const COUNT_CELL_CLASS = 'text-body-sm-semibold text-foreground';
export const COUNT_CELL_MUTED_CLASS = 'text-body-sm-semibold text-muted-foreground';

function cellText(count: CountDisplay, format: (n: number) => string): string {
  if (count.label) return count.label;
  if (count.value === null) return EMPTY_TEXT;
  return format(count.value);
}

interface EngagementCountCellProps {
  count: CountDisplay;
  /** Number formatter; defaults to Indian grouping (4,90,248). */
  format?: (n: number) => string;
  /** "short" for narrow columns: the date stays in the tooltip. */
  captionLength?: CaptionLength;
  className?: string;
}

/** One count cell for every screen: a number or a label, the previous count under it, and an explanation when there's one. */
export function EngagementCountCell({
  count,
  format = formatIntegerIN,
  captionLength = 'full',
  className,
}: EngagementCountCellProps) {
  const caption = previousCaption(count.previous, format, captionLength);
  const layout = cn('inline-flex flex-col items-end', className);
  const content = (
    <>
      <span className="tabular-nums">{cellText(count, format)}</span>
      {caption && <span className="text-caption-medium text-muted-foreground whitespace-nowrap">{caption}</span>}
    </>
  );
  if (!count.tooltip) return <span className={layout}>{content}</span>;
  return (
    <HintTrigger hint={count.tooltip} className={layout}>
      {content}
    </HintTrigger>
  );
}
